import type { InfiniteData, QueryClient } from '@tanstack/react-query'
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import type { Database } from '@/types/supabase'
import { supabase } from '@/lib/supabase/client'
import { fetchAllRows } from '@/lib/supabase/fetch-all-rows'
import { searchWords } from '@/lib/supabase/search-filter'
import type { Generation } from './generations'
import type { Tag } from './tags'

// =============================================================================
// Scene Types
// =============================================================================

type SceneRow = Database['public']['Tables']['scenes']['Row']

/**
 * Scene with current generation and author profile.
 * Scenes are domain entities; generations are their versions/transformations.
 * current_generation embeds the full Generation including its author profile.
 */
export interface Scene extends SceneRow {
  profiles: {
    id: string
    email: string
    full_name: string | null
    avatar_url: string | null
  } | null
  current_generation: Generation | null
  scene_tags: { tags: Tag }[]
}

const PAGE_SIZE = 16
const MIN_SEARCH_WORD_LENGTH = 2

export interface SceneFilters {
  authorId?: string | null
  modelId?: string | null
  searchQuery?: string | null
  tagIds?: string[] | null
}

/**
 * Apply scene filters to any Supabase query builder.
 * Works with both select and update queries.
 */
function applySceneFilters<
  T extends {
    eq: (col: string, val: string) => T
    is: (col: string, val: null) => T
    ilike: (col: string, val: string) => T
    contains: (col: string, val: string[]) => T
  },
>(query: T, filters?: SceneFilters): T {
  let q = query

  if (filters?.authorId) {
    q = q.eq('author', filters.authorId)
  }

  // Every selected label must be present.
  if (filters?.tagIds && filters.tagIds.length > 0) {
    q = q.contains('tag_ids', filters.tagIds)
  }

  if (filters?.modelId) {
    q = q.eq('model_id', filters.modelId)
  }

  if (filters?.searchQuery) {
    for (const word of searchWords(
      filters.searchQuery,
      MIN_SEARCH_WORD_LENGTH
    )) {
      q = q.ilike('name', `%${word}%`)
    }
  }

  return q
}

export const scene = {
  findOne: (sceneId: string) =>
    queryOptions({
      queryKey: ['scene', sceneId],
      queryFn: async (): Promise<Scene | null> => {
        if (!sceneId || sceneId.trim() === '') {
          return null
        }

        const { data, error } = await supabase
          .from('scenes')
          .select(
            `
            *,
            profiles!author(id, email, full_name, avatar_url),
            current_generation:generations!current_generation_id(*, profiles!author(id, email, full_name, avatar_url)),
            scene_tags(tags(id, name, color))
          `
          )
          .eq('id', sceneId)
          .is('deleted_at', null)
          .maybeSingle()

        if (error) {
          throw new Error(`Failed to fetch scene: ${error.message}`, {
            cause: error,
          })
        }

        return data as Scene | null
      },
    }),

  findManyByWorkspace: (
    workspaceId: string,
    filters?: SceneFilters,
    options?: { pageSize?: number }
  ) =>
    infiniteQueryOptions({
      queryKey: [
        'scenes',
        workspaceId,
        filters ?? {},
        options?.pageSize ?? PAGE_SIZE,
      ],
      queryFn: async ({ pageParam = 0 }): Promise<Scene[]> => {
        if (!workspaceId || workspaceId.trim() === '') {
          throw new Error(
            'Invalid workspaceId: cannot fetch scenes without valid workspace ID'
          )
        }

        const pageSize = options?.pageSize ?? PAGE_SIZE
        const from = pageParam * pageSize
        const to = from + pageSize - 1

        const baseQuery = supabase
          .from('scenes')
          .select(
            `
            *,
            profiles!author(id, email, full_name, avatar_url),
            current_generation:generations!current_generation_id(*, profiles!author(id, email, full_name, avatar_url)),
            scene_tags(tags(id, name, color))
          `
          )
          .eq('workspace_id', workspaceId)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })
          .range(from, to)

        const { data, error } = await applySceneFilters(baseQuery, filters)

        if (error) {
          throw new Error(`Failed to fetch scenes: ${error.message}`, {
            cause: error,
          })
        }

        return (data || []) as Scene[]
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) => {
        const pageSize = options?.pageSize ?? PAGE_SIZE
        return lastPage.length === pageSize ? allPages.length : undefined
      },
    }),

  fetchAllByWorkspace: async (
    workspaceId: string,
    filters?: SceneFilters
  ): Promise<Scene[]> => {
    if (!workspaceId || workspaceId.trim() === '') {
      return []
    }

    const scenes = await fetchAllRows(
      (from, to) =>
        applySceneFilters(
          supabase
            .from('scenes')
            .select(
              `
              *,
              profiles!author(id, email, full_name, avatar_url),
              current_generation:generations!current_generation_id(*, profiles!author(id, email, full_name, avatar_url)),
              scene_tags(tags(id, name, color))
            `
            )
            .eq('workspace_id', workspaceId)
            .is('deleted_at', null),
          filters
        )
          .order('created_at', { ascending: false })
          .order('id', { ascending: false })
          .range(from, to),
      'Failed to fetch scenes'
    )

    return scenes as Scene[]
  },

  findManyCompletedByWorkspace: (
    workspaceId: string,
    options?: { searchQuery?: string | null }
  ) =>
    infiniteQueryOptions({
      queryKey: [
        'scenes',
        workspaceId,
        'completed',
        options?.searchQuery ?? null,
        PAGE_SIZE,
      ],
      queryFn: async ({ pageParam = 0 }): Promise<Scene[]> => {
        if (!workspaceId || workspaceId.trim() === '') {
          throw new Error(
            'Invalid workspaceId: cannot fetch scenes without valid workspace ID'
          )
        }

        const from = pageParam * PAGE_SIZE
        const to = from + PAGE_SIZE - 1

        let query = supabase
          .from('scenes')
          .select(
            `
            *,
            profiles!author(id, email, full_name, avatar_url),
            current_generation:generations!inner!current_generation_id(*, profiles!author(id, email, full_name, avatar_url)),
            scene_tags(tags(id, name, color))
          `
          )
          .eq('current_generation.status', 'completed')
          .not('current_generation.storage_path', 'is', null)
          .eq('workspace_id', workspaceId)
          .is('deleted_at', null)
          .order('created_at', { ascending: false })

        if (options?.searchQuery) {
          for (const word of searchWords(
            options.searchQuery,
            MIN_SEARCH_WORD_LENGTH
          )) {
            query = query.ilike('name', `%${word}%`)
          }
        }

        const { data, error } = await query.range(from, to)

        if (error) {
          throw new Error(`Failed to fetch scenes: ${error.message}`, {
            cause: error,
          })
        }

        return (data || []) as unknown as Scene[]
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.length === PAGE_SIZE ? allPages.length : undefined,
    }),

  /**
   * Scenes shared with the organisation, from every workspace in it. Only
   * finished ones, since a shared scene is there to be used.
   */
  findManySharedByOrg: (
    orgId: string,
    options?: { searchQuery?: string | null }
  ) =>
    infiniteQueryOptions({
      queryKey: [
        'scenes',
        'org',
        orgId,
        'shared',
        options?.searchQuery ?? null,
        PAGE_SIZE,
      ],
      queryFn: async ({ pageParam = 0 }): Promise<Scene[]> => {
        if (!orgId || orgId.trim() === '') {
          throw new Error(
            'Invalid orgId: cannot fetch shared scenes without an organization ID'
          )
        }

        const from = pageParam * PAGE_SIZE
        const to = from + PAGE_SIZE - 1

        let query = supabase
          .from('scenes')
          .select(
            `
            *,
            profiles!author(id, email, full_name, avatar_url),
            current_generation:generations!inner!current_generation_id(*, profiles!author(id, email, full_name, avatar_url)),
            scene_tags(tags(id, name, color))
          `
          )
          .eq('org_id', orgId)
          .not('shared_at', 'is', null)
          .eq('current_generation.status', 'completed')
          .not('current_generation.storage_path', 'is', null)
          .is('deleted_at', null)
          .order('shared_at', { ascending: false })

        if (options?.searchQuery) {
          for (const word of searchWords(
            options.searchQuery,
            MIN_SEARCH_WORD_LENGTH
          )) {
            query = query.ilike('name', `%${word}%`)
          }
        }

        const { data, error } = await query.range(from, to)

        if (error) {
          throw new Error(`Failed to fetch shared scenes: ${error.message}`, {
            cause: error,
          })
        }

        return (data || []) as unknown as Scene[]
      },
      initialPageParam: 0,
      getNextPageParam: (lastPage, allPages) =>
        lastPage.length === PAGE_SIZE ? allPages.length : undefined,
    }),

  /** Shares a scene with its organisation, or stops sharing it. */
  setShared: async (sceneId: string, shared: boolean): Promise<void> => {
    const { error } = await supabase
      .from('scenes')
      .update({ shared_at: shared ? new Date().toISOString() : null })
      .eq('id', sceneId)

    if (error) {
      throw new Error(
        `Failed to ${shared ? 'share' : 'stop sharing'} the scene: ${error.message}`,
        { cause: error }
      )
    }
  },

  /**
   * Update scene's current generation (called after generation completes)
   */
  updateCurrentGeneration: async (
    sceneId: string,
    generationId: string
  ): Promise<void> => {
    const { error } = await supabase
      .from('scenes')
      .update({ current_generation_id: generationId })
      .eq('id', sceneId)

    if (error) {
      throw new Error(
        `Failed to update scene current generation: ${error.message}`,
        { cause: error }
      )
    }
  },

  /**
   * Update scene metadata (name)
   */
  update: async (sceneId: string, input: { name?: string }): Promise<void> => {
    const { error } = await supabase
      .from('scenes')
      .update({
        ...(input.name !== undefined && { name: input.name }),
      })
      .eq('id', sceneId)

    if (error) {
      throw new Error(`Failed to update scene: ${error.message}`, {
        cause: error,
      })
    }
  },

  /**
   * Soft delete a scene
   */
  delete: async (sceneId: string): Promise<void> => {
    if (!sceneId || sceneId.trim() === '') {
      throw new Error('Invalid sceneId: cannot delete scene without valid ID')
    }

    const { error } = await supabase
      .from('scenes')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', sceneId)

    if (error) {
      throw new Error(`Failed to delete scene: ${error.message}`, {
        cause: error,
      })
    }
  },

  /**
   * Soft delete all scenes in a workspace (with optional filters)
   */
  /**
   * Soft delete multiple scenes by IDs
   */
  deleteMany: async (ids: string[]): Promise<void> => {
    if (!ids.length) {
      return
    }

    const { error } = await supabase
      .from('scenes')
      .update({ deleted_at: new Date().toISOString() })
      .in('id', ids)

    if (error) {
      throw new Error(`Failed to delete scenes: ${error.message}`, {
        cause: error,
      })
    }
  },

  deleteAllByWorkspace: async (
    workspaceId: string,
    filters?: SceneFilters,
    excludeIds?: string[]
  ): Promise<void> => {
    if (!workspaceId || workspaceId.trim() === '') {
      return
    }

    let baseQuery = supabase
      .from('scenes')
      .update({ deleted_at: new Date().toISOString() })
      .eq('workspace_id', workspaceId)
      .is('deleted_at', null)

    baseQuery = applySceneFilters(baseQuery, filters)

    if (excludeIds && excludeIds.length > 0) {
      baseQuery = baseQuery.not('id', 'in', `(${excludeIds.join(',')})`)
    }

    const { error } = await baseQuery

    if (error) {
      throw new Error(`Failed to delete scenes: ${error.message}`, {
        cause: error,
      })
    }
  },
}

/**
 * Find a scene in any workspace infinite query cache.
 * For use in route loaders (non-hook context).
 */
export function findCachedScene(
  queryClient: QueryClient,
  workspaceId: string,
  sceneId: string
): Scene | null {
  const queries = queryClient.getQueriesData<InfiniteData<Scene[]>>({
    queryKey: ['scenes', workspaceId],
    exact: false,
  })

  for (const [, data] of queries) {
    const found = data?.pages.flat().find((s) => s.id === sceneId)
    if (found) {
      return found
    }
  }
  return null
}
